// lib/app-carte.js — LA MAISON, DÉCRITE. Ce que l'opérateur doit savoir de
// l'application pour y vivre plutôt que de la regarder de l'extérieur.
//
// ══ L'EXIGENCE (Dimitri, 2026-09-08) ════════════════════════════════════════
//
//   « Je ne veux pas seulement qu'il voie mes données. Je veux qu'il vive DANS
//     l'application et qu'il la comprenne. Je suis sur la fiche d'un logement,
//     je lui demande "pourquoi ça ne marche pas" — aujourd'hui il ne sait pas
//     de quoi je parle. Il devrait voir ce que je vois. C'est ça, le cerveau
//     de l'app. Pas un observateur de la base — un habitant qui connaît la
//     maison. »
//
// ══ CE QUE CE FICHIER EST, ET N'EST PAS ════════════════════════════════════
//
// Il PORTE le savoir : à quoi sert chaque écran, quels réglages y vivent, ce
// qui s'y règle, quels leviers y agissent. Ce savoir n'existe nulle part
// ailleurs — il n'est pas dans la base, il est dans le code, sous une forme
// qu'aucun modèle ne peut lire de façon fiable.
//
// Il ne PORTE PAS la liste des écrans. Celle-là est DÉRIVÉE de `CATEGORIES`
// dans src/App.jsx, qui en est la source unique. Un test vérifie que la carte
// décrit exactement les écrans qui existent — ni plus (un écran inventé
// enverrait Dimitri au mauvais endroit), ni moins (un écran ajouté sans être
// décrit rendrait l'opérateur muet dessus sans que personne le voie).
//
// ══ POURQUOI CE TEST EST LA MOITIÉ DU TRAVAIL ══════════════════════════════
//
// ⚠️ UNE CARTE QUI DÉRIVE EST PIRE QUE PAS DE CARTE. Elle fait croire qu'on a
// regardé. Le dépôt en porte déjà la preuve : au 2026-09-08, deux commentaires
// décrivaient `direct_messages_save` et `direct_messages_settings` comme
// « dormants » alors qu'ils servent les mails réels du parcours Direct depuis
// la bascule ADR-276 du 8 juillet. Un humain qui les lisait pour juger de
// l'innocuité d'un geste se trompait aussi.
//
// Donc : chaque affirmation vérifiable de ce fichier EST vérifiée par
// src/lib/app-carte.test.js — les écrans contre src/App.jsx, les fichiers
// contre le disque, les leviers contre lib/operateur-leviers.js.
//
// Ce qui ne se vérifie pas mécaniquement — le `quoi`, le `sert_a` — est écrit
// au présent et décrit l'ÉTAT ACTUEL, jamais un projet (règle permanente
// Dimitri, R5 : « les textes d'aide décrivent l'état réel au moment de
// l'écriture, jamais des phases futures qui périment en silence »).
//
// ══ CE QU'ON N'ÉCRIT PAS ICI ═══════════════════════════════════════════════
//
// Aucune donnée : pas de nom de logement, pas de montant, pas de valeur de
// réglage. La carte dit OÙ ça se règle, jamais CE QUI est réglé — sinon elle
// devient un cache qui ment dès la première modification.

/**
 * La maison, pièce par pièce.
 *
 *   cle      — l'identifiant d'onglet (celui de src/App.jsx, `tab`)
 *   groupe   — la destination du menu qui le contient
 *
 * ⚠️ Ce champ s'appelait `famille` — renommé le 2026-09-08 parce que `famille`
 * est AUSSI un nom d'argument de levier (`veille_silence`). La collision n'était
 * pas théorique : la sentinelle qui interdit au contexte d'écran de porter une
 * clé d'argument de geste l'a attrapée. Deux sens pour un même mot, sur deux
 * chemins qui ne doivent jamais se toucher, c'est une confusion qui finit par
 * se réaliser.
 *   sert_a   — à quoi sert cet écran, en une phrase, du point de vue de Dimitri
 *   reglages — ce qui s'y RÈGLE (et donc ce qui peut y manquer)
 *   fichiers — où ça vit dans src/ (vérifié contre le disque)
 */
const ECRANS = Object.freeze({
  // ── Ce qu'il consulte ─────────────────────────────────────────────────────
  accueil: {
    groupe: "accueil",
    sert_a:
      "Voir ce qui attend, ce matin : les arrivées, les départs, ce qui coince.",
    reglages: [],
    fichiers: ["src/views/AccueilAdmin.jsx"],
  },
  operateur: {
    groupe: "hors-menu",
    sert_a:
      "Décider de ce que l'opérateur propose, et voir ce qu'il a fait. Ce n'est pas une destination : elle s'ouvre depuis le bandeau d'en-tête.",
    reglages: ["coupe-circuit (en marche / arrêté)", "niveau d'autonomie"],
    fichiers: ["src/views/operateur/OperateurView.jsx"],
  },
  params: {
    groupe: "hors-menu",
    sert_a:
      "Les réglages de la maison : coordonnées de la conciergerie, comptes, séries de factures, réseaux sociaux.",
    reglages: [
      "coordonnées de contact (cfg.co) — source unique, reprise partout",
      "comptes et rôles",
      "préfixes de séries de factures",
    ],
    fichiers: ["src/tabs/ParamsPanel.jsx"],
  },

  // ── Aujourd'hui ───────────────────────────────────────────────────────────
  calendrier: {
    groupe: "jour",
    sert_a:
      "Le planning : qui arrive, qui part, ce qui est bloqué, à quel prix.",
    reglages: ["prix par date", "blocages", "verrous 📌 par date"],
    fichiers: ["src/tabs/CalendarPanel.jsx"],
  },
  menages: {
    groupe: "jour",
    sert_a: "Le pilotage des ménages du jour : qui fait quoi, à quelle heure.",
    reglages: ["heure fixée d'un ménage", "agent assigné"],
    fichiers: ["src/views/MenageTodayView.jsx"],
  },
  "menage-controle": {
    groupe: "jour",
    sert_a:
      "Contrôler le travail fait : désigner un contrôleur, juger, demander une reprise.",
    reglages: ["contrôleur par défaut d'un logement"],
    fichiers: [
      "src/views/menage/ControleView.jsx",
      "src/views/jour/JourControleCtrl.jsx",
    ],
  },
  incidents: {
    groupe: "jour",
    sert_a: "Ce qui a été cassé ou signalé : qualifier, décider d'un rachat.",
    reglages: [],
    fichiers: [
      "src/views/jour/JourIncidentsCtrl.jsx",
      "src/views/MenageIncidentsView.jsx",
    ],
  },
  "orchestrateur-horaires": {
    groupe: "jour",
    sert_a:
      "Les demandes d'heure des voyageurs : accorder un créneau, ou le refuser.",
    reglages: ["créneaux proposés et accordés"],
    fichiers: ["src/views/OrchestrateurHorairesView.jsx"],
  },
  stock: {
    groupe: "jour",
    sert_a:
      "Les consommables : ce qui reste, ce qui a été déposé, ce qui manque.",
    reglages: ["seuils d'alerte par article", "prix unitaires"],
    fichiers: ["src/tabs/StockTab.jsx", "src/views/jour/JourStockCtrl.jsx"],
  },
  "mes-taches": {
    groupe: "jour",
    sert_a: "Les tâches personnelles, hors ménage.",
    reglages: [],
    fichiers: [
      "src/tabs/MesTachesTab.jsx",
      "src/views/jour/JourTachesCtrl.jsx",
    ],
  },

  // ── Argent ────────────────────────────────────────────────────────────────
  donnees: {
    groupe: "argent",
    sert_a: "Les réservations et ce qu'elles rapportent, mois par mois.",
    reglages: [],
    fichiers: ["src/views/argent/ArgentChrome.jsx"],
  },
  facturation: {
    groupe: "argent",
    sert_a:
      "Ce qui est dû aux propriétaires et ce que FBZ prend, mois par mois.",
    reglages: [
      "source de facturation par logement (kv | v2)",
      "surcharges, suppressions, déplacements et prorata d'une ligne",
    ],
    fichiers: ["src/views/argent/ArgentChrome.jsx"],
  },
  historique: {
    groupe: "argent",
    sert_a: "Les factures et reçus déjà émis.",
    reglages: [],
    fichiers: ["src/tabs/HistoriquePanel.jsx"],
  },
  compta: {
    groupe: "argent",
    sert_a: "Les charges et les écritures comptables.",
    reglages: ["règles de charges récurrentes"],
    fichiers: ["src/tabs/ComptaTab.jsx"],
  },

  // ── Logements ─────────────────────────────────────────────────────────────
  logements: {
    groupe: "logements",
    sert_a:
      "Les fiches des logements : leurs réglages, leur livret, leurs instructions, et la file « à démarcher ».",
    reglages: [
      "tarification : prix de base, plancher, multiplicateurs par canal",
      "caution : montant et seuil de nuits",
      "compte Stripe du logement",
      "barème de taxe de séjour de la commune",
      "instructions : adresse, codes, wifi, accès, règles de la maison",
      "livret d'accueil : modules, FAQ, photos, langues",
      "éligibilité du copilote IA, et ce qu'il a le droit de divulguer",
    ],
    fichiers: [
      "src/tabs/logements/InfoFbzTab.jsx",
      "src/tabs/logements/_ficheUi.jsx",
    ],
  },

  // ── Voyageurs ─────────────────────────────────────────────────────────────
  messages: {
    groupe: "voyageurs",
    sert_a:
      "La boîte de réception : les messages des voyageurs, et les brouillons du copilote.",
    reglages: ["envoi réel par logement", "instructions données au copilote"],
    fichiers: [
      "src/tabs/MessagesPanel.jsx",
      "src/tabs/messages/InboxPanel.jsx",
    ],
  },
  "voyageurs-textes": {
    groupe: "voyageurs",
    sert_a:
      "Les messages automatiques : ce qui part tout seul, quand, et dans quel parcours.",
    reglages: ["gabarits par étape", "décalage en jours et heure d'envoi"],
    fichiers: ["src/tabs/MessagesPanel.jsx"],
  },
  "voyageurs-avis": {
    groupe: "voyageurs",
    sert_a:
      "Les avis reçus, et ceux épinglés sur la page publique d'un logement.",
    reglages: ["avis épinglés (6 au plus par logement)"],
    fichiers: ["src/views/voyageurs/VoyageursAvisCtrl.jsx"],
  },
  "voyageurs-demandes": {
    groupe: "voyageurs",
    sert_a:
      "Les demandes de réservation directe : les valider, les refuser, suivre leur parcours.",
    reglages: [],
    fichiers: ["src/views/voyageurs/VoyageursDemandesCtrl.jsx"],
  },
});

/** @returns {object|null} la description d'un écran, ou null s'il est inconnu. */
function lireEcran(cle) {
  if (typeof cle !== "string") return null;
  return Object.prototype.hasOwnProperty.call(ECRANS, cle) ? ECRANS[cle] : null;
}

/**
 * Tous les réglages de la maison, à plat, avec l'écran où chacun se pose.
 * C'est ce qui permet de répondre « ça se règle dans Logements » sans deviner.
 */
function reglagesParEcran() {
  const out = [];
  for (const [cle, e] of Object.entries(ECRANS)) {
    for (const r of e.reglages) out.push({ ecran: cle, reglage: r });
  }
  return out;
}

module.exports = { ECRANS, lireEcran, reglagesParEcran };
